import {announce} from '@github-ui/aria-live'
import {caseInsensitiveStringCompare} from '@github-ui/filter-core'
import {
  addFilterValue,
  type FilterQualifier,
  getFilterValues,
  MAX_QUERY_LENGTH,
  removeFilterValue,
} from '@github-ui/filter-query'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {ListFilterButton} from '@github-ui/list-filter-button'
import {ActionBarButtonAnchor} from '@github-ui/list-view/ActionBarButtonAnchor'
import {useQuery} from '@github-ui/react-query'
import {useDebounce} from '@github-ui/use-debounce'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {CircleSlashIcon, PinIcon} from '@primer/octicons-react'
import {SelectPanel, type SelectPanelItemInput, Tooltip} from '@primer/react'
import {useCallback, useEffect, useMemo, useState} from 'react'

import {
  decodeWorkflowSuggestion,
  isWorkflowRunWorkflowSuggestion,
  type WorkflowRunActorSuggestion,
  type WorkflowRunBranchSuggestion,
} from './workflow-runs-filter-providers'
import {filterKeys, repoActionsPath, type WorkflowRunsFilterKey} from './WorkflowRunsFilter'
import {useActionsAnalytics} from '../../hooks/use-actions-analytics'

type WorkflowRunsHeaderFilterItem = SelectPanelItemInput & {id: string; text: string}
const EMPTY_HEADER_FILTER_ITEMS: WorkflowRunsHeaderFilterItem[] = []
const QUERY_TOO_COMPLEX_MESSAGE = 'Filter menus are not available for complex queries.'
const SELECTION_TOO_COMPLEX_MESSAGE = 'Adding this filter would make your query too long. Use the filter input instead.'

interface WorkflowRunsHeaderFiltersProps {
  owner: string
  repo: string
  query: string
  selectedWorkflow: boolean
  onQueryChange: (query: string) => void
}

const headerFilterEndpoints: Record<WorkflowRunsFilterKey, string> = {
  workflow: 'workflows',
  event: 'events',
  is: 'statuses',
  branch: 'branches',
  actor: 'actors',
}

// Rendered as Primer ActionBar items so the filters are visible in the server-rendered HTML. Primer handles overflow
// with CSS, unlike @github-ui/action-bar, which hides every item until it measures them after hydration.
export function WorkflowRunsHeaderFilters({
  owner,
  repo,
  query,
  selectedWorkflow,
  onQueryChange,
}: WorkflowRunsHeaderFiltersProps) {
  const keys: WorkflowRunsFilterKey[] = selectedWorkflow
    ? ['event', 'is', 'branch', 'actor']
    : ['workflow', 'event', 'is', 'branch', 'actor']

  return keys.map(key => (
    <WorkflowRunsHeaderFilter
      key={key}
      filterKey={key}
      owner={owner}
      repo={repo}
      query={query}
      onQueryChange={onQueryChange}
    />
  ))
}

function WorkflowRunsHeaderFilter({
  filterKey,
  owner,
  repo,
  query,
  onQueryChange,
}: {
  filterKey: WorkflowRunsFilterKey
  owner: string
  repo: string
  query: string
  onQueryChange: (query: string) => void
}) {
  const track = useActionsAnalytics()
  const qualifier = useMemo(() => workflowRunsFilterQualifier(filterKey), [filterKey])
  const querySelectedValues = useMemo(() => getFilterValues(query, qualifier), [qualifier, query])
  const queryExcludedValues = useMemo(() => getFilterValues(query, qualifier, {isNegated: true}), [qualifier, query])
  const [open, setOpen] = useState(false)
  const [filter, setFilter] = useState('')
  const [requestFilter, setRequestFilter] = useState('')
  const [draftSelectedValues, setDraftSelectedValues] = useState(querySelectedValues)
  const [selectionTooComplex, setSelectionTooComplex] = useState(false)
  const announceSelectionTooComplex = useCallback((element: HTMLSpanElement | null) => {
    if (element) announce(SELECTION_TOO_COMPLEX_MESSAGE, {element})
  }, [])
  const updateRequestFilter = useDebounce((value: string) => setRequestFilter(value), 200)
  const label = filterKeys[filterKey].displayName ?? filterKey
  const queryTooComplex = query.length > MAX_QUERY_LENGTH
  // These short-lived legacy JSON suggestions are scoped to the open picker and should not persist.
  // eslint-disable-next-line @github-ui/github-monorepo/require-query-persister, @github-ui/github-monorepo/require-query-schema
  const itemsQuery = useQuery({
    queryKey: ['workflow-runs-header-filter', owner, repo, filterKey, requestFilter],
    enabled: open && !queryTooComplex,
    queryFn: async () => {
      const search = new URLSearchParams({q: requestFilter})
      const response = await reactFetchJSON(
        `${repoActionsPath(owner, repo, headerFilterEndpoints[filterKey])}?${search.toString()}`,
      )
      if (!response.ok) throw new Error(`Unable to load ${label.toLowerCase()} filters.`)

      return workflowRunsHeaderFilterItems(filterKey, await response.json())
    },
    staleTime: 60_000,
  })
  const items = itemsQuery.data ?? EMPTY_HEADER_FILTER_ITEMS
  const selectedValues = open ? draftSelectedValues : querySelectedValues
  const selected = useMemo(
    () =>
      selectedValues.map(
        value =>
          items.find(item => workflowRunsFilterValuesEqual(filterKey, item.id, value)) ??
          workflowRunsHeaderFilterItem(filterKey, value),
      ),
    [filterKey, items, selectedValues],
  )
  const orderedItems = useMemo(() => {
    const prioritizedValues = [...querySelectedValues, ...queryExcludedValues].filter(
      (value, index, values) =>
        values.findIndex(other => workflowRunsFilterValuesEqual(filterKey, other, value)) === index,
    )
    return [
      ...prioritizedValues.map(
        value =>
          items.find(item => workflowRunsFilterValuesEqual(filterKey, item.id, value)) ??
          workflowRunsHeaderFilterItem(filterKey, value),
      ),
      ...items.filter(
        item =>
          !prioritizedValues.some(prioritizedValue =>
            workflowRunsFilterValuesEqual(filterKey, prioritizedValue, item.id),
          ),
      ),
    ].map(item =>
      queryExcludedValues.some(value => workflowRunsFilterValuesEqual(filterKey, value, item.id))
        ? {
            ...item,
            'aria-label': [item.text, item.description, 'Excluded'].filter(Boolean).join(', '),
            trailingVisual: () => <CircleSlashIcon aria-hidden="true" data-testid="excluded-filter-icon" />,
          }
        : item,
    )
  }, [filterKey, items, queryExcludedValues, querySelectedValues])

  useEffect(() => {
    if (itemsQuery.isError) announce(`Unable to load ${label.toLowerCase()} filters. Try again.`)
  }, [itemsQuery.isError, label])

  return (
    <SelectPanel
      title={`Filter by ${label}`}
      inputLabel={`Filter ${label.toLowerCase()} options`}
      open={open && !queryTooComplex}
      onOpenChange={nextOpen => {
        if (queryTooComplex) {
          setOpen(false)
          return
        }

        setOpen(nextOpen)
        if (nextOpen) {
          track('workflow_runs.open_filter', `ref_loc:actions_workflow_runs;filter:${filterKey}`)
          setDraftSelectedValues(querySelectedValues)
          setSelectionTooComplex(false)
        } else {
          const nextQuery = applyHeaderFilterSelection({
            query,
            filterKey,
            qualifier,
            querySelectedValues,
            queryExcludedValues,
            draftSelectedValues,
          }).query
          if (nextQuery !== query) {
            track('workflow_runs.apply_filter', `ref_loc:actions_workflow_runs;filter:${filterKey}`)
            onQueryChange(nextQuery)
          }
          updateRequestFilter.cancel()
          setFilter('')
          setRequestFilter('')
          setSelectionTooComplex(false)
        }
      }}
      filterValue={filter}
      onFilterChange={value => {
        setFilter(value)
        updateRequestFilter(value)
      }}
      items={orderedItems}
      selected={selected}
      onSelectedChange={(nextSelected: SelectPanelItemInput[]) => {
        const nextSelectedValues = nextSelected.map(item => String(item.id))
        const {exceedsLimit} = applyHeaderFilterSelection({
          query,
          filterKey,
          qualifier,
          querySelectedValues,
          queryExcludedValues,
          draftSelectedValues: nextSelectedValues,
        })
        setSelectionTooComplex(exceedsLimit)
        if (!exceedsLimit) setDraftSelectedValues(nextSelectedValues)
      }}
      virtualized
      loading={itemsQuery.isFetching}
      notice={
        selectionTooComplex
          ? {
              variant: 'warning',
              text: <span ref={announceSelectionTooComplex}>{SELECTION_TOO_COMPLEX_MESSAGE}</span>,
            }
          : undefined
      }
      overlayProps={{maxHeight: 'large'}}
      message={
        itemsQuery.isError
          ? {
              variant: 'error',
              title: `Unable to load ${label.toLowerCase()} filters`,
              body: 'Try again.',
            }
          : undefined
      }
      renderAnchor={({children, ...anchorProps}) => {
        const buttonProps = {
          ...anchorProps,
          as: ListFilterButton,
          isActionBarItem: true,
          'aria-expanded': open && !queryTooComplex,
          'aria-haspopup': 'dialog' as const,
          inactive: queryTooComplex,
          disabled: queryTooComplex,
          onClick: queryTooComplex ? undefined : anchorProps.onClick,
          onKeyDown: queryTooComplex ? undefined : anchorProps.onKeyDown,
        }
        const button = <ActionBarButtonAnchor {...buttonProps}>{label}</ActionBarButtonAnchor>

        return queryTooComplex ? (
          <Tooltip text={QUERY_TOO_COMPLEX_MESSAGE} type="description">
            {button}
          </Tooltip>
        ) : (
          button
        )
      }}
    />
  )
}

function applyHeaderFilterSelection({
  query,
  filterKey,
  qualifier,
  querySelectedValues,
  queryExcludedValues,
  draftSelectedValues,
}: {
  query: string
  filterKey: WorkflowRunsFilterKey
  qualifier: FilterQualifier
  querySelectedValues: string[]
  queryExcludedValues: string[]
  draftSelectedValues: string[]
}): {query: string; exceedsLimit: boolean} {
  let nextQuery = query
  for (const value of querySelectedValues) {
    if (!draftSelectedValues.some(draftValue => workflowRunsFilterValuesEqual(filterKey, draftValue, value))) {
      nextQuery = removeFilterValue(nextQuery, value, qualifier)
    }
  }
  for (const value of queryExcludedValues) {
    if (draftSelectedValues.some(draftValue => workflowRunsFilterValuesEqual(filterKey, draftValue, value))) {
      nextQuery = removeFilterValue(nextQuery, value, qualifier, {isNegated: true})
    }
  }
  for (const value of draftSelectedValues) {
    if (!querySelectedValues.some(queryValue => workflowRunsFilterValuesEqual(filterKey, queryValue, value))) {
      const updatedQuery = addFilterValue(nextQuery, value, qualifier)
      // We have already excluded values present in the original selection so an unchanged query means length has been exceeded.
      if (updatedQuery === nextQuery) return {query, exceedsLimit: true}
      nextQuery = updatedQuery
    }
  }

  return {query: nextQuery, exceedsLimit: false}
}

function workflowRunsHeaderFilterItems(
  filterKey: WorkflowRunsFilterKey,
  response: unknown,
): WorkflowRunsHeaderFilterItem[] {
  if (!Array.isArray(response)) throw new Error('Workflow run filter response must be an array.')

  switch (filterKey) {
    case 'workflow':
      return response.flatMap(value => {
        const suggestion =
          typeof value === 'string'
            ? {name: value, pinned: false}
            : isWorkflowRunWorkflowSuggestion(value)
              ? value
              : undefined
        if (!suggestion) return []

        const item = workflowRunsHeaderFilterItem(filterKey, decodeWorkflowSuggestion(suggestion.name))
        return [
          suggestion.pinned
            ? {
                ...item,
                trailingVisual: () => <PinIcon aria-label="Pinned" data-testid="pinned-workflow-icon" />,
              }
            : item,
        ]
      })
    case 'event':
      return response
        .filter((value): value is string => typeof value === 'string')
        .map(value => workflowRunsHeaderFilterItem(filterKey, value))
    case 'is':
      return response
        .filter((value): value is string => typeof value === 'string')
        .map(value => workflowRunsHeaderFilterItem(filterKey, value))
    case 'branch': {
      let foundDefaultBranch = false
      return response.flatMap(value => {
        if (!isBranchSuggestion(value)) return []

        const isDefaultBranch = value.default && !foundDefaultBranch
        if (isDefaultBranch) foundDefaultBranch = true
        return [
          {
            id: value.name,
            text: value.name,
            trailingVisual: isDefaultBranch ? () => <span>Default</span> : undefined,
          },
        ]
      })
    }
    case 'actor':
      return response.flatMap(value =>
        isActorSuggestion(value)
          ? [
              {
                id: value.login,
                text: value.login,
                leadingVisual: () => <GitHubAvatar src={value.avatarUrl} size={20} alt="" />,
                description: value.displayName && value.displayName !== value.login ? value.displayName : undefined,
                descriptionVariant: 'inline',
              },
            ]
          : [],
      )
  }
}

function workflowRunsHeaderFilterItem(filterKey: WorkflowRunsFilterKey, value: string): WorkflowRunsHeaderFilterItem {
  return {
    id: value,
    text: filterKey === 'is' ? capitalizeFilterValue(value) : value,
  }
}

function workflowRunsFilterValuesEqual(filterKey: WorkflowRunsFilterKey, left: string, right: string) {
  return filterKey === 'branch' ? left === right : caseInsensitiveStringCompare(left, right)
}

function isBranchSuggestion(value: unknown): value is WorkflowRunBranchSuggestion {
  return (
    typeof value === 'object' &&
    value !== null &&
    'name' in value &&
    typeof value.name === 'string' &&
    'default' in value &&
    typeof value.default === 'boolean'
  )
}

function isActorSuggestion(value: unknown): value is WorkflowRunActorSuggestion {
  return (
    typeof value === 'object' &&
    value !== null &&
    'login' in value &&
    typeof value.login === 'string' &&
    'displayName' in value &&
    (typeof value.displayName === 'string' || value.displayName === null) &&
    'avatarUrl' in value &&
    typeof value.avatarUrl === 'string'
  )
}

function capitalizeFilterValue(value: string) {
  const normalized = value.replaceAll('_', ' ')
  return normalized.charAt(0).toUpperCase() + normalized.slice(1)
}

function workflowRunsFilterQualifier(filterKey: WorkflowRunsFilterKey): FilterQualifier {
  const {key, aliases} = filterKeys[filterKey]
  return {key, aliases}
}
